import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["cards", "formWrapper", "wrapper"]
  static values = {
    errorLoadingForm: String,
    errorSubmittingForm: String,
    errorDeletingHint: String,
    errorGeneric: String
  }

  connect() {
    // used to track the card being edited so we can replace it later
    this.editingHintCard = null
  }

  async startNew(event) {
    event.preventDefault()
    event.stopPropagation()

    try {
      const response = await fetch("/admin/localization/hints/new", {
        headers: { Accept: "text/html" }
      })

      if (!response.ok) throw new Error()

      const html = await response.text()
      this.formWrapperTarget.innerHTML = html

      this.preprocessPronunciations(this.formWrapperTarget)

      // Ensure the form has the submit action if not already set
      const form = this.formWrapperTarget.querySelector("form")
      this.ensureSubmitAction(form)

      // Open the wrapper using the external "toggle" stimulus controller
      const toggleController = this.application.getControllerForElementAndIdentifier(this.wrapperTarget, "toggle")
      if (toggleController) toggleController.toggle()

      // Manually focus input after render; browser blocks autofocus if another element is already focused.
      requestAnimationFrame(() => {
        const wordInput = this.formWrapperTarget.querySelector("[name='account[word]']")
        if (wordInput) wordInput.focus()
      })
    } catch (error) {
      alert(this.errorLoadingFormValue)
    }
  }

  async edit(event) {
    event.preventDefault()

    const hintId = event.target.dataset.hintId || event.target.closest("[data-hint-id]")?.dataset.hintId

    if (!hintId) return

    const hintElement = this.cardsTarget.querySelector(`[data-hint-id="${hintId}"]`)
    if (hintElement) {
      this.editingHintCard = hintElement.closest(".wg-card-component") || hintElement.closest("article")
    }

    if (!this.editingHintCard) return

    const response = await fetch(`/admin/localization/hints/${hintId}/edit`, {
      headers: { Accept: "text/html" }
    })

    if (!response.ok) return

    const html = await response.text()

    // Insert the edit form just after the card, and hide the card
    const formContainer = document.createElement("div")
    formContainer.innerHTML = html
    formContainer.classList.add("editing-form-container")

    // Temporarily hide the original card so we can bring it back if cancel is clicked
    this.editingHintCard.style.display = "none"
    this.editingHintCard.insertAdjacentElement("afterend", formContainer)

    this.preprocessPronunciations(formContainer)

    const form = formContainer.querySelector("form")
    this.ensureSubmitAction(form)

    // Manually focus input after render; browser blocks autofocus if another element is already focused.
    requestAnimationFrame(() => {
      const wordInput = form.querySelector("[name='account[word]']")
      if (wordInput) wordInput.focus()
    })
  }

  async submitForm(event) {
    event.preventDefault()
    event.stopPropagation()

    const form = event.target
    if (form.dataset.submitting === "true") return // prevent double submission
    form.dataset.submitting = "true"

    this.preprocessPronunciations(form)

    try {
      const formData = new FormData(form)
      const response = await fetch(form.action, {
        method: form.method.toUpperCase(),
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
        },
        body: formData
      })

      const data = await response.json()

      if (!data.success) return alert(data.error || this.errorGenericValue)

      const container = document.createElement("div")
      container.innerHTML = data.html
      const newCard = container.firstElementChild

      if (this.editingHintCard && newCard) {
        this.editingHintCard.replaceWith(newCard) // Editing: replace the hidden card with the updated version
        this.editingHintCard = null
      } else if (newCard) {
        this.cardsTarget.appendChild(newCard) // Adding new: append the new card to the list (newest at bottom)
      }

      this.cancel() // Close the form and reset state
    } catch (error) {
      alert(this.errorSubmittingFormValue)
    } finally {
      form.dataset.submitting = "false" // Allow future submissions by unsetting the guard flag
    }
  }

  async delete(event) {
    event.preventDefault()

    const button = event.target.closest("[data-hint-id]")
    if (!button) return

    const hintId = button.dataset.hintId
    const template = button.dataset.deleteConfirmTemplate
    const hintWord = button.dataset.hintWord

    if (!hintId || !template) return;

    const message = template.replace("%{word}", hintWord || "");

    if (!confirm(message)) {
      return;
    }

    try {
      const response = await fetch(`/admin/localization/hints/${hintId}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
        }
      })

      const data = await response.json()

      if (data.success) {
        const card = this.cardsTarget.querySelector(`[data-hint-id="${hintId}"]`)
          ?.closest(".wg-card-component, article")
        if (card) card.remove()
      } else {
        alert(this.errorDeletingHintValue)
      }
    } catch {
      alert(this.errorGenericValue)
    }
  }

  cancel() {
    const editingForm = document.querySelector(".editing-form-container")
    if (editingForm) {
      editingForm.remove()

      // Show the card again if we had hidden it for inline editing
      this.editingHintCard?.style.removeProperty("display")
      this.editingHintCard = null
      return
    }

    this.formWrapperTarget.innerHTML = ""
    const toggleController = this.application.getControllerForElementAndIdentifier(this.wrapperTarget, "toggle")
    if (toggleController?.targetValue === true) toggleController.toggle()
  }

  // Parse comma-separated string into array format for submission
  preprocessPronunciations(form = null) {
    const container = form ?? this.formWrapperTarget
    if (!container) return

    const text = container.querySelector("[data-hints-target='pronunciationsText']")
    const hidden = container.querySelector("[data-hints-target='pronunciationsArray']")

    if (text && hidden) {
      hidden.value = text.value.split(",").map(p => p.trim()).filter(Boolean)
    }
  }

  ensureSubmitAction(form) {
    if (form && !form.dataset.action) {
      form.dataset.action = "submit->hints#submitForm"
    }
  }

  // toggle intensity slider visibility based on checkbox state
  toggleIntensity(event) {
    const checked = event.target.checked
    const wrapper = event.target.closest("form")?.querySelector("[data-hints-target='intensityWrapper']")
    const hiddenField = event.target.closest("form")?.querySelector("[data-hints-target='customIntensityEnabledField']")
    if (wrapper) wrapper.style.display = checked ? "" : "none"
    if (hiddenField) hiddenField.value = checked ? "1" : "0"
  }
}
